import {
  PRODUCTION_AVATAR_OPTIONS,
  avatarAccentColor,
  generateDrugAvatar,
  type AvatarAccentColor,
} from "./avatar-recipe"
import { assetSeed, nameSeed } from "./avatar-seed"

/**
 * Module-level memo of seed -> SVG data URI at the production recipe.
 *
 * Keyed by seed only: the SVG scales, so display size is not part of identity.
 * Capped so a long session cannot grow it without bound; the oldest insertion
 * is evicted first (Map preserves insertion order).
 */

const CACHE_CAP = 2000
const cache = new Map<string, string>()

export function avatarDataUriForSeed(seed: string): string {
  const hit = cache.get(seed)
  if (hit !== undefined) return hit
  const { dataUri } = generateDrugAvatar(seed, PRODUCTION_AVATAR_OPTIONS)
  if (cache.size >= CACHE_CAP) {
    const oldest = cache.keys().next()
    if (!oldest.done) cache.delete(oldest.value)
  }
  cache.set(seed, dataUri)
  return dataUri
}

/**
 * The one place that holds the identity rule: a canonical asset ID seeds the
 * avatar; only when none resolves does the normalised name seed a fallback.
 * Used by the DrugAvatar component and by background-image call sites alike.
 */
export function avatarUriForDrug(assetId: string | null, name: string): string {
  return avatarDataUriForSeed(seedForDrug(assetId, name))
}

/** The identity rule as a seed: asset ID when one resolves, else the name. */
export function seedForDrug(assetId: string | null, name: string): string {
  return assetId ? assetSeed(assetId) : nameSeed(name)
}

/**
 * Module-level memo of seed -> accent triple, same cap and eviction as the
 * URI cache. `avatarAccentColor` builds a whole recipe per call, and a chip
 * row asks for the same seed on every render (109 rows on Changes), so the
 * triple is computed once per seed and the SAME object is handed back after.
 */
const accentCache = new Map<string, AvatarAccentColor>()

export function accentForSeed(seed: string): AvatarAccentColor {
  const hit = accentCache.get(seed)
  if (hit !== undefined) return hit
  const accent = avatarAccentColor(seed)
  if (accentCache.size >= CACHE_CAP) {
    const oldest = accentCache.keys().next()
    if (!oldest.done) accentCache.delete(oldest.value)
  }
  accentCache.set(seed, accent)
  return accent
}

/**
 * The sphere's accent for a drug under the SAME seed rule as
 * `avatarUriForDrug`, so a chip beside a sphere is that sphere's own colour
 * by construction. The one place the hook (`useDrugColor`) and the readouts
 * calendar helper both read, so the seed rule has no third copy.
 */
export function accentForDrug(assetId: string | null, name: string): AvatarAccentColor {
  return accentForSeed(seedForDrug(assetId, name))
}

export const _internals = {
  cache,
  accentCache,
  CACHE_CAP,
}
